import type { Pointer } from './platform';

type Point = { x: number; y: number };
export type CanvasBoard = Point & { size: number };
const clamp = (n: number, low: number, high: number) => Math.max(low, Math.min(high, n));
const rotate = (p: Point, angle: number): Point => ({ x: p.x * Math.cos(angle) - p.y * Math.sin(angle), y: p.x * Math.sin(angle) + p.y * Math.cos(angle) });
const angleDelta = (angle: number) => Math.atan2(Math.sin(angle), Math.cos(angle));

/** Local paper transform shared by both drawing modes. It never changes saved artwork. */
export class CanvasView {
  zoom = 1;
  rotation = 0;
  pan: Point = { x: 0, y: 0 };
  private touches = new Map<number, Point>();
  private captured = false;
  private pair?: { ids: number[]; distance: number; angle: number; zoom: number; rotation: number; anchor: Point };
  get capturing() { return this.captured; }
  get gesturing() { return this.captured && this.touches.size >= 2; }
  get changed() { return Math.abs(this.zoom - 1) > .001 || Math.abs(angleDelta(this.rotation)) > .001 || Math.hypot(this.pan.x, this.pan.y) > .5; }
  get degrees() { return Math.round(angleDelta(this.rotation) * 180 / Math.PI); }
  reset() { this.zoom = 1; this.rotation = 0; this.pan = { x: 0, y: 0 }; this.clearGesture(); }
  clearGesture() { this.touches.clear(); this.pair = undefined; this.captured = false; }
  contains(b: CanvasBoard, p: Point) { return p.x >= b.x && p.x <= b.x + b.size && p.y >= b.y && p.y <= b.y + b.size; }
  toDocument(b: CanvasBoard, p: Point, bounded = false): Point {
    const local = rotate({ x: p.x - b.x - b.size / 2 - this.pan.x, y: p.y - b.y - b.size / 2 - this.pan.y }, -this.rotation);
    const x = 500 + local.x * 1000 / (b.size * this.zoom); const y = 500 + local.y * 1000 / (b.size * this.zoom);
    return bounded ? { x: clamp(x, 0, 1000), y: clamp(y, 0, 1000) } : { x, y };
  }
  toScreen(b: CanvasBoard, p: Point): Point {
    const local = rotate({ x: (p.x - 500) * b.size * this.zoom / 1000, y: (p.y - 500) * b.size * this.zoom / 1000 }, this.rotation);
    return { x: b.x + b.size / 2 + this.pan.x + local.x, y: b.y + b.size / 2 + this.pan.y + local.y };
  }
  containsPaper(b: CanvasBoard, p: Point) {
    const local = this.toDocument(b, p);
    return this.contains(b, p) && local.x >= 0 && local.x <= 1000 && local.y >= 0 && local.y <= 1000;
  }
  /** Clip before transforming so paper and previews never cover the tools. */
  apply(ctx: CanvasRenderingContext2D, b: CanvasBoard) {
    ctx.beginPath(); ctx.rect(b.x, b.y, b.size, b.size); ctx.clip();
    ctx.translate(b.x + b.size / 2 + this.pan.x, b.y + b.size / 2 + this.pan.y);
    ctx.rotate(this.rotation); ctx.scale(b.size * this.zoom / 1000, b.size * this.zoom / 1000); ctx.translate(-500, -500);
  }
  render(ctx: CanvasRenderingContext2D, b: CanvasBoard, buffer: HTMLCanvasElement) {
    ctx.save(); ctx.fillStyle = '#E4E8E7'; ctx.fillRect(b.x, b.y, b.size, b.size);
    this.apply(ctx, b); ctx.imageSmoothingEnabled = true; ctx.drawImage(buffer, 0, 0, 1000, 1000); ctx.restore();
    if (this.gesturing) {
      const width = Math.min(180, b.size - 12); const x = b.x + (b.size - width) / 2;
      ctx.save(); ctx.fillStyle = 'rgba(255,255,255,0.9)'; ctx.fillRect(x, b.y + 8, width, 24);
      ctx.fillStyle = '#52625F'; ctx.font = '12px sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(`${Math.round(this.zoom * 100)}% · ${this.degrees}°`, b.x + b.size / 2, b.y + 20); ctx.restore();
    }
  }
  private rebase(b: CanvasBoard) {
    const entries = [...this.touches];
    if (entries.length < 2) { this.pair = undefined; return; }
    const [[idA, a], [idB, c]] = entries;
    this.pair = { ids: [idA, idB], distance: Math.hypot(c.x - a.x, c.y - a.y), angle: Math.atan2(c.y - a.y, c.x - a.x),
      zoom: this.zoom, rotation: this.rotation, anchor: this.toDocument(b, { x: (a.x + c.x) / 2, y: (a.y + c.y) / 2 }) };
  }
  private keepPaperVisible(b: CanvasBoard) {
    // Keep a usable part of the paper in the viewport, including after rotation.
    const local = rotate(this.pan, -this.rotation); const half = b.size * this.zoom / 2;
    const nearest = { x: clamp(local.x, -half, half), y: clamp(local.y, -half, half) };
    const dx = local.x - nearest.x; const dy = local.y - nearest.y; const distance = Math.hypot(dx, dy);
    const limit = b.size / 2 - Math.min(40, b.size * .15);
    if (distance <= limit) return false;
    this.pan = rotate({ x: nearest.x + dx * limit / distance, y: nearest.y + dy * limit / distance }, this.rotation); return true;
  }
  pointer(kind: 'down' | 'move' | 'up' | 'cancel', p: Pointer, b: CanvasBoard): { consumed: boolean; started: boolean } {
    let consumed = this.captured;
    // Native touches is the complete current contact set, not just positions.
    // End/cancel can be lost across overlays or page changes. Never let an old
    // contact turn a later single finger into a pinch, including reused IDs.
    if (p.touches) {
      const active = new Set(p.touches.map(touch => touch.id));
      for (const id of this.touches.keys()) if (!active.has(id) || (kind === 'down' && id === p.id)) {
        this.touches.delete(id);
        if (this.pair?.ids.includes(id)) this.pair = undefined;
      }
      if (!this.touches.size) {
        this.clearGesture();
        if (kind === 'down') consumed = false;
      }
    }
    if (kind === 'down' && (this.contains(b, p) || this.captured)) this.touches.set(p.id, p);
    else if (kind === 'move' && this.touches.has(p.id)) this.touches.set(p.id, p);
    // WeChat reports both fingers in one native sample. Update them atomically;
    // evaluating a mixture of this frame and the previous frame causes wobble.
    for (const touch of p.touches ?? []) if (this.touches.has(touch.id)) this.touches.set(touch.id, touch);
    if (kind === 'up' || kind === 'cancel') this.touches.delete(p.id);
    if (this.touches.size < 2) {
      this.pair = undefined;
      if (!this.touches.size) this.captured = false;
      return { consumed, started: false };
    }
    const started = !this.captured; this.captured = true;
    if (!this.pair || this.pair.ids.some(id => !this.touches.has(id))) { this.rebase(b); return { consumed: true, started }; }
    if (kind !== 'move' || !this.pair.ids.includes(p.id)) return { consumed: true, started };
    const start = this.pair; const [a, c] = start.ids.map(id => this.touches.get(id)!);
    const distance = Math.hypot(c.x - a.x, c.y - a.y);
    // Nearly coincident fingers have an undefined angle. Rebase when separated.
    if (distance < 8 || start.distance < 8) { this.rebase(b); return { consumed: true, started }; }
    const angle = Math.atan2(c.y - a.y, c.x - a.x);
    this.rotation = start.rotation + angleDelta(angle - start.angle);
    const requested = start.zoom * distance / start.distance; this.zoom = clamp(requested, .25, 4);
    const offset = rotate({ x: (start.anchor.x - 500) * b.size * this.zoom / 1000, y: (start.anchor.y - 500) * b.size * this.zoom / 1000 }, this.rotation);
    this.pan = { x: (a.x + c.x) / 2 - b.x - b.size / 2 - offset.x, y: (a.y + c.y) / 2 - b.y - b.size / 2 - offset.y };
    const limited = this.keepPaperVisible(b);
    // At an edge, reverse immediately instead of requiring a long dead travel.
    if (limited || requested !== this.zoom) this.rebase(b);
    return { consumed: true, started };
  }
}

export function canvasPointer(p: Pointer, scale: number, top: number): Pointer {
  const map = (point: Point & { id: number }) => ({ id: point.id, x: point.x / scale, y: point.y / scale - top });
  return { ...map(p), ...(p.touches ? { touches: p.touches.map(map) } : {}) };
}
